<template>
  <div class="container mt-5 hide-print">
       <div v-if="boardGame" class="board-game-details">
      <div class="row">
        <div class="col-12">
          <div class="mb-4 game-header-image-container">
            <img 
              :src="boardGame.headerImageUrl || getGameImage(boardGame.type)" 
              class="card-img-top"
              :alt="`${boardGame.name} board game`"
            >
          <!--   <div class="card-body">
              <h1 class="card-title mb-3">{{ boardGame.name }} Cards</h1>
              <div>Creating cards for board game: 
          <strong>
          <router-link 
          :to="{ name: 'BoardGameDetail', params: { id: boardGame.id } }" 
          class=""
        >{{ boardGame.name }}</router-link></strong> </div>
            </div> -->

          </div>
        </div>
      </div>
    </div>
    <!--     <div v-if="boardGame" class="alert alert-info mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          Creating cards for board game: 
          <strong>{{ boardGame.name }}</strong>
        </div>
        <router-link 
          :to="{ name: 'BoardGameDetail', params: { id: boardGame.id } }" 
          class="btn btn-sm btn-outline-primary"
        >
          View Game Details
        </router-link>
      </div>
    </div>
     -->
    
    <!-- Permission notice for non-editors -->
    <div v-if="boardGame && !canEdit" class="alert alert-info mb-3">
      <p class="mb-0">
        <i class="fas fa-info-circle me-2"></i>
        You are viewing cards for <strong>{{ boardGame.name }}</strong>. 
        Only the owner and editors can create or edit cards.
      </p>
    </div>

   <!-- AI Image Generator Modal -->
  <!-- Teleported to <body> deliberately. theme.css sets
       `#app > * { position: relative; z-index: 1 }` to lift page content above
       the paper texture, which makes every routed page a stacking context. A
       modal left inside it is capped at that z-index, while Bootstrap appends
       .modal-backdrop to <body> at z-index 1050 — so the backdrop painted OVER
       the dialog and the screen just went dark with nothing to click. -->
  <Teleport to="body">
    <div class="modal fade" id="aiImageGeneratorModal" tabindex="-1" aria-labelledby="aiImageGeneratorModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="aiImageGeneratorModalLabel">Generate AI Card Image</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="aiImagePrompt" class="form-label">Image Prompt</label>
              <textarea 
                id="aiImagePrompt" 
                class="form-control" 
                v-model="aiImagePrompt" 
                placeholder="Describe the image you want to generate"
                rows="4"
              ></textarea>
            </div>
            <div class="text-center mb-3" v-if="aiImageGenerating">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Generating...</span>
              </div>
              <p>Generating AI images...</p>
            </div>
            <div v-else-if="aiImageResults.length > 0" class="row g-3">
              <div 
                v-for="(imageUrl, index) in aiImageResults" 
                :key="index" 
                class="col-4"
              >
                <div 
                  class="ai-image-option" 
                  :class="{ 'selected': selectedAIImage === imageUrl }"
                  @click="selectedAIImage = imageUrl"
                >
                  <img 
                    :src="imageUrl" 
                    class="img-fluid rounded" 
                    :alt="`AI Generated Image ${index + 1}`"
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="generateAIImages"
              :disabled="!aiImagePrompt.trim()"
            >
              Generate Images
            </button>
            <button 
              type="button" 
              class="btn btn-success" 
              @click="selectAIImage"
              :disabled="!selectedAIImage"
              data-bs-dismiss="modal"
            >
              Use Selected Image
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <div class="container mt-1">
    <!-- Header. On a phone the title takes the full width and the actions sit
         on one scrollable row rather than stacking three buttons down the page. -->
    <div class="page-head hide-print">
      <h3 class="page-title">Cards {{ boardGame ? `for ${boardGame.name}` : '' }}</h3>
      <div class="page-actions">
        <button v-if="canEdit" class="btn btn-success btn-sm" @click="toggleBuildCard">
          {{ showBuildCard ? 'Hide builder' : 'Build card' }}
        </button>
        <button class="btn btn-outline-secondary btn-sm" @click="togglePrintMode">
          {{ isPrintMode ? 'Exit print view' : 'Print view' }}
        </button>
        <button class="btn btn-primary btn-sm" :disabled="!boardGameId || Boolean(downloading)"
                @click="downloadDeck('color')">
          {{ downloading === 'color' ? 'Preparing…' : 'Download colour' }}
        </button>
        <button class="btn btn-outline-primary btn-sm" :disabled="!boardGameId || Boolean(downloading)"
                title="A cheaper copy: white cards, black type, greyscale art"
                @click="downloadDeck('bw')">
          {{ downloading === 'bw' ? 'Preparing…' : 'Download black & white' }}
        </button>
      </div>
    </div>

    <p v-if="boardGameId && downloadSelection().filtered" class="small text-muted mb-2 hide-print">
      Downloads print the {{ downloadSelection().ids.length }} cards shown, not the whole deck.
    </p>

    <!-- Mobile: flip between the editor and the deck instead of scrolling past
         the whole builder to reach the cards. Hidden on desktop, where both are
         visible at once and a switch would only get in the way. -->
    <div v-if="canEdit && boardGameId" class="mode-switch d-md-none hide-print" role="tablist">
      <button type="button" class="mode-btn" :class="{ active: mobileMode === 'edit' }"
              role="tab" :aria-selected="mobileMode === 'edit'"
              @click="setMobileMode('edit')">
        Edit
      </button>
      <button type="button" class="mode-btn" :class="{ active: mobileMode === 'deck' }"
              role="tab" :aria-selected="mobileMode === 'deck'"
              @click="setMobileMode('deck')">
        Deck <span class="mode-count">{{ filteredCards.length }}</span>
      </button>
    </div>

    <div class="bd-callout bd-callout-info mb-3" :class="{ 'd-none d-md-block': mobileMode === 'edit' }">
      <div class="d-flex justify-content-between">
        <div class="d-none d-md-block">
          <strong>Total Cards:</strong> {{ cards.length }} |
          <strong>Filtered Cards:</strong> {{ filteredCards.length }} |
          <strong>Total Variants:</strong> {{ cards.filter(c => c.parent_id).length }} |
          <strong>Filtered Variants:</strong> {{ filteredCards.filter(c => c.parent_id).length }}
        </div>
        <!-- Phone: one line. The filtered counts only earn their space once a
             filter is actually on, and variants only once the deck has any. -->
        <div class="d-md-none counts-compact">
          <strong>{{ filteredCards.length }}</strong>
          <span v-if="filteredCards.length !== cards.length"> of {{ cards.length }}</span>
          cards
          <span v-if="cards.filter(c => c.parent_id).length">
            · <strong>{{ filteredCards.filter(c => c.parent_id).length }}</strong> variants
          </span>
        </div>
        <div>
          <div class="form-check form-check-inline">
            <input class="form-check-input" type="checkbox" id="excludeVariants" v-model="excludeVariants" @change="filterCards">
            <label class="form-check-label" for="excludeVariants">
              <span class="d-none d-md-inline">Exclude Variants</span>
              <span class="d-md-none">No variants</span>
            </label>
          </div>
          <button class="btn btn-sm btn-outline-secondary ms-2" @click="resetExclusions">
            <span class="d-none d-md-inline">Reset Exclusions</span>
            <span class="d-md-none">Reset</span>
          </button>
        </div>
      </div>
    </div>

     <!-- Card Builder -->
    <div class="row" v-if="showBuildCard"
         :class="{ 'd-none d-md-flex': mobileMode === 'deck' }">
      <h1 class="text-left mb-4">{{ canEdit ? 'Build Card' : 'View Cards' }}</h1>
      <div class="col-md-6" v-if="canEdit">
        <div class="card-designer">
          <div class="card-header">Card Creator</div>
          <div class="">
            <form @submit.prevent="createCard">
              <div class="mb-3">
                <label for="cardName" class="form-label small">Card Name</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm" 
                  id="cardName" 
                  v-model="previewCard.name"
                  @keyup="updatePreview"
                >
              </div>
              <div class="mb-3">
                <label for="cardType" class="form-label small">Type</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm" 
                  id="cardType" 
                  v-model="previewCard.type"
                  @keyup="updatePreview"
                >
              </div>
              <div class="mb-3">
                <label for="cardSubtype" class="form-label small">Subtype</label>
                <input 
                  type="text" 
                  class="form-control form-control-sm" 
                  id="cardSubtype" 
                  v-model="previewCard.subtype"
                  @keyup="updatePreview"
                >
              </div>
              <div class="mb-3">
                <label for="cardDescription" class="form-label small">Description</label>
                <textarea 
                  class="form-control form-control-sm" 
                  id="cardDescription" 
                  rows="3"
                  v-model="previewCard.description"
                  @keyup="updatePreview"
                ></textarea>
              </div>
              <div class="mb-3">
                <label for="cardFlavorText" class="form-label small">Flavor Text</label>
                <textarea 
                  class="form-control form-control-sm" 
                  id="cardFlavorText" 
                  rows="2"
                  v-model="previewCard.flavorText"
                  @keyup="updatePreview"
                ></textarea>
              </div>
              <div class="mb-3">
                <label for="cardRepeats" class="form-label small">Card Repeats</label>
                <input 
                  type="number" 
                  class="form-control form-control-sm" 
                  id="cardRepeats" 
                  v-model="previewCard.repeats"
                  min="1"
                  @keyup="updatePreview"
                >
                <small class="form-text text-muted">Number of times this card should be included in the deck</small>
              </div>
              <div class="mb-3">
                <label for="cardIcon" class="form-label small">Card Icon 
                  <a 
                    href="https://nagoshiashumari.github.io/Rpg-Awesome/" 
                    target="_blank" 
                    class="ms-2 small text-primary"
                  >
                    Icon list
                  </a>
                </label>
                <Multiselect 
                  v-model="previewCard.icon"
                  :options="iconOptions"
                  :searchable="true"
                  placeholder="Select an icon"
                  label="label"
                  valueProp="value"
                >
                  <template v-slot:option="{ option }">
                    <div class="d-flex align-items-center">
                      <i :class="['ra', option.value, 'me-2']" style="font-size: 1.5em;"></i>
                      {{ option.label }}
                    </div>
                  </template>
                </Multiselect>
              </div>
              
              <div class="mb-3">
                <label class="form-label small">Card Background Image</label>
                <div class="d-flex gap-2 mb-2">
                  <input 
                    type="file" 
                    ref="cardImageInput"
                    @change="uploadCardImage"
                    accept="image/*"
                    class="d-none"
                  >
                  <button 
                    type="button"
                    class="btn btn-sm btn-outline-primary" 
                    @click="triggerImageUpload"
                  >
                    <i class="fas fa-upload me-1"></i> Upload Image
                  </button>
                  <button 
                    type="button"
                    class="btn btn-sm btn-outline-secondary" 
                    data-bs-toggle="modal" 
                    data-bs-target="#aiImageGeneratorModal"
                    @click="updatePrompt" 
                  >
                    <i class="fas fa-magic me-1"></i> Generate AI Image
                  </button>
                </div>
                <div v-if="previewCard.image_url" class="card-image-preview">
                  <img :src="previewCard.image_url" class="img-thumbnail" style="max-height: 100px;" />
                  <button 
                    type="button"
                    class="btn btn-sm btn-danger mt-1" 
                    @click="removeCardImage"
                  >
                    Remove Image
                  </button>
                </div>
              </div>
              <div class="d-flex gap-2 form-submit-row">
                <button 
                  type="submit" 
                  :class="isEditMode ? 'btn btn-danger' : 'btn btn-primary'"
                >
                  {{ isEditMode ? 'Update Card' : 'Create Card' }}
                </button>
                <button 
                  v-if="isEditMode"
                  type="button"
                  class="btn btn-secondary me-2"
                  @click="clearForm"
                >
                  Clear Card
                </button>
                <button 
                  v-if="isEditMode"
                  type="button"
                  class="btn btn-danger"
                  @click="confirmDeleteCard"
                >
                  Delete Card
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div class="col-md-6" :class="{ 'offset-md-3': !canEdit }">
        <div class="card-designer">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>{{ canEdit ? 'Card Preview' : 'Card Viewer' }}</span>
            <button v-if="canEdit && boardGameId" type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="showDesignPanel = !showDesignPanel">
              {{ showDesignPanel ? 'Close design' : 'Card design' }}
            </button>
          </div>
          <div class="preview-body">
            <!-- The SAME renderer the print PDF uses, so what is on screen is
                 what comes out of the printer. -->
            <CardPreview
              v-if="boardGameId"
              :game-id="boardGameId"
              :card="previewCard"
              :builtin="designOverride.builtin"
              :style-override="designOverride.style"
            />
            <div v-else class="text-muted small p-3">
              Open this page from a board game to preview cards.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <div v-if="showDesignPanel && canEdit && boardGameId" class="row hide-print">
      <div class="col-12">
        <div class="card-designer mb-4">
          <div class="card-header">Card Design — applies to every card in this deck</div>
          <div class="p-3">
            <CardDesignPanel
              :game-id="boardGameId"
              :sample-card="designSample"
              :card-types="deckCardTypes"
              @change="designOverride = $event"
              @saved="onDesignSaved"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row g-2 mb-3 align-items-center hide-print"
         :class="{ 'd-none d-md-flex': mobileMode === 'edit' }">
      <div class="col-md-8">
        <div class="input-group">
          <input 
            type="text" 
            class="form-control" 
            placeholder="Search cards"
            v-model="searchQuery"
            @keyup="filterCards"
          >
          <div class="input-group-append">
            <div class="dropdown">
              <button 
                class="btn btn-primary dropdown-toggle" 
                type="button" 
                id="searchFieldDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
              >
                {{ searchFieldLabel }}
              </button>
              <ul class="dropdown-menu" aria-labelledby="searchFieldDropdown">
                <li>
                  <a 
                    class="dropdown-item" 
                    href="#" 
                    @click.prevent="searchField = ''"
                  >
                    All Fields
                  </a>
                </li>
                <li>
                  <a 
                    class="dropdown-item" 
                    href="#" 
                    @click.prevent="searchField = 'name'"
                  >
                    Name
                  </a>
                </li>
                <li>
                  <a 
                    class="dropdown-item" 
                    href="#" 
                    @click.prevent="searchField = 'type'"
                  >
                    Type
                  </a>
                </li>
                <li>
                  <a 
                    class="dropdown-item" 
                    href="#" 
                    @click.prevent="searchField = 'subtype'"
                  >
                    Subtype
                  </a>
                </li>
                <li>
                  <a 
                    class="dropdown-item" 
                    href="#" 
                    @click.prevent="searchField = 'description'"
                  >
                    Description
                  </a>
                </li>
                <li>
                  <a 
                    class="dropdown-item" 
                    href="#" 
                    @click.prevent="searchField = 'flavorText'"
                  >
                    Flavor Text
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- The deck. Every tile is the SAME renderer the print PDF uses, so the
         grid is a true proof sheet rather than an HTML approximation of one.
         Tiles render lazily as they scroll into view. -->
    <div v-if="boardGameId" class="deck-grid"
         :class="{ 'print-mode': isPrintMode, 'd-none d-md-grid': mobileMode === 'edit' }">
      <CardTile
        v-for="(card, i) in filteredCards"
        :key="card.id"
        :index="i"
        :game-id="boardGameId"
        :card="card"
        :can-edit="canEdit"
        :excluded="excludedFromPrint.has(card.id)"
        :design-version="designVersion"
        @open="canEdit ? editCard($event) : viewCard($event)"
        @toggle-print="toggleExcludeFromPrint($event)"
        @variants="viewCardDetails($event)"
      />
    </div>
    <div v-if="filteredCards.length === 0" class="no-results text-center text-muted py-5">
      No cards found matching your search.
    </div>
  </div>
</template>

<script>
import testCards from '@/data/test-cards.json'
import icons from '@/data/icons.json'
import Multiselect from '@vueform/multiselect'
import CardPreview from '@/components/CardPreview.vue'
import CardDesignPanel from '@/components/CardDesignPanel.vue'
import CardTile from '@/components/CardTile.vue'
import '@vueform/multiselect/themes/default.css'
import { ref, onMounted, computed } from 'vue'
import { auth, firestore, storage } from '@/firebase'
import { saveCard, updateCard, loadUserCards, generateGameImage, loadBoardGameCards, getEditors, deleteCard } from '@/firebase'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useRoute } from 'vue-router'
import { doc, getDoc } from 'firebase/firestore'
import { sparkforgeApi } from '@/sparkforgeApi'

export default {
  name: 'BuildCardsPage',
   components: {
     Multiselect,
     CardPreview,
     CardDesignPanel,
     CardTile
  },
  setup() {
    const user = ref(null);
    const cards = ref([]);
    const isLoading = ref(false);
    const route = useRoute();
    const editors = ref([]);
    const isLoadingPermissions = ref(false);
    const showBuildCard = ref(false);
    var filteredCards = ref([]);
    // const filteredCards = ref([]);

    // Board game filter details
    const boardGameId = ref(route.query.boardGameId || null);
    const boardGame = ref(null);
    
    // Permission flags
    const isOwner = computed(() => {
      return user.value && boardGame.value && user.value.uid === boardGame.value.userId;
    });

    const isEditor = computed(() => {
      if (!user.value || !user.value.email || !editors.value.length) return false;
      
      const userEditor = editors.value.find(
        editor => editor.email === user.value.email && editor.status === 'accepted'
      );
      
      return !!userEditor;
    });

    const canEdit = computed(() => {
      return isOwner.value || isEditor.value;
    });

    const loadBoardGame = async () => {
      if (!boardGameId.value) return null;

      try {
        const boardGameRef = doc(firestore, 'boardGames', boardGameId.value);
        const boardGameSnap = await getDoc(boardGameRef);

        if (boardGameSnap.exists()) {
          return {
            id: boardGameSnap.id,
            ...boardGameSnap.data()
          };
        }
        return null;
      } catch (error) {
        console.error('Error loading board game:', error);
        return null;
      }
    };
    
    const loadBoardGameEditors = async () => {
      if (!boardGameId.value) return;
      
      isLoadingPermissions.value = true;
      try {
        editors.value = await getEditors(boardGameId.value);
      } catch (error) {
        console.error('Error loading editors:', error);
        editors.value = [];
      } finally {
        isLoadingPermissions.value = false;
      }
    };

    onMounted(async () => {
      const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
        user.value = currentUser;
        
        // Load board game details if board game ID is present
        if (boardGameId.value) {
          boardGame.value = await loadBoardGame();
          await loadBoardGameEditors();
        }

        // Load cards regardless of user authentication status
        await loadCards();
      });

      return () => unsubscribe();
    });

    const loadCards = async () => {
      isLoading.value = true;
      try {
        if (boardGameId.value) {
          // Load all cards for this board game regardless of user
          cards.value = await loadBoardGameCards(boardGameId.value);
          filteredCards.value = [...cards.value];
          // console.log(...cards.value)
          console.log(filteredCards)
        } else if (user.value) {
          // If no board game ID but user is logged in, load user's cards
          const userCards = await loadUserCards(user.value.uid);
          cards.value = userCards.length > 0 ? userCards : testCards;
        } else {
          // Fallback to test cards for non-authenticated users without board game ID
          cards.value = testCards;
        }
      } catch (error) {
        console.error('Error loading cards:', error);
        cards.value = testCards;
      } finally {
        isLoading.value = false;
      }
    };
    const toggleBuildCard = () => {
      showBuildCard.value = !showBuildCard.value;
    };

    return {
      filteredCards,
      user,
      cards,
      isLoading,
      loadCards,
      boardGameId,
      boardGame,
      isOwner,
      isEditor,
      canEdit,
      showBuildCard,
      toggleBuildCard
    };
  },
  async created() {
    // Check if we're in edit mode (accessed via EditCard route)
    if (this.$route.name === 'EditCard' && this.$route.params.cardId) {
      try {
        const cardDoc = doc(firestore, 'cards', this.$route.params.cardId);
        const cardSnap = await getDoc(cardDoc);
        
        if (cardSnap.exists()) {
          const cardData = {
            id: cardSnap.id,
            ...cardSnap.data()
          };
          
          // Set up the card for editing
          this.editCard(cardData);
          
          // Set board game ID from the card data or query parameter
          const boardGameId = cardData.boardGameId || this.$route.query.boardGameId;
          if (boardGameId) {
            this.boardGameId = boardGameId;
          }
        }
      } catch (error) {
        console.error('Error loading card for editing:', error);
      }
    }
  },

  data() {
    return {
      icons: icons,
      // filteredCards: [],
      iconCategories: Object.keys(icons),
      excludeVariants: false,
      excludedFromPrint: new Set(),
      downloading: null,
      cardPrintsJob: null,
      iconOptions: Object.entries(icons).flatMap(([category, categoryIcons]) => 
        categoryIcons.map(icon => ({
          label: `${category} - ${icon}`,
          value: icon
        }))
      ),
      previewCard: {
        id: null,
        name: '',
        description: '',
        type: '',
        subtype: '',
        flavorText: '',
        icon: '',
        repeats: 1,
        image_url: null,
        prompt: ''
      },
      isEditMode: false,
      // Card design: the panel emits live overrides so the preview updates
      // before anything is saved.
      // Mobile only: which half of the page is showing. Desktop ignores this
      // and shows both, which is why every use is paired with a d-md-* class.
      mobileMode: 'deck',
      showDesignPanel: false,
      designOverride: { builtin: null, style: null },
      // Bumped on save so every tile re-renders with the new design.
      designVersion: 0,
      searchQuery: '',
      searchField: '',
      isPrintMode: false,
      isUploadingImage: false,
      aiImagePrompt: '',
      aiImageGenerating: false,
      aiImageResults: [],
      selectedAIImage: null
    };
  },
  computed: {
    /** A real card for the design thumbnails — choosing a layout on lorem
     *  ipsum tells you nothing about your own deck. */
    designSample() {
      const withText = (this.cards || []).find((c) => (c.description || '').length > 40);
      return withText || this.cards?.[0] || {};
    },
    deckCardTypes() {
      return [...new Set((this.cards || []).map((c) => c.type).filter(Boolean))].sort();
    },
    searchFieldLabel() {
      const fieldLabels = {
        '': 'All Fields',
        'name': 'Name',
        'type': 'Type',
        'subtype': 'Subtype',
        'description': 'Description',
        'flavorText': 'Flavor Text'
      };
      return fieldLabels[this.searchField] || 'All Fields';
    }
  },
  async mounted() {
    // Signed-in state arrives after mount; the prints need it.
    if (auth.authStateReady) await auth.authStateReady();
    this.prepareCardPrints();
  },
  methods: {
    setMobileMode(mode) {
      this.mobileMode = mode;
      // "Edit" has to actually show the editor. The builder sits behind
      // showBuildCard, so without this the tab switched to a blank page and
      // you had to find "Build card" as well.
      if (mode === 'edit') this.showBuildCard = true;
      // Jump back to the top: flipping to the deck and landing halfway down
      // the old view is disorienting.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onDesignSaved() {
      // Saved design is now the deck default, so stop overriding it locally
      // and let every tile re-render against it.
      this.designOverride = { builtin: null, style: null };
      this.designVersion += 1;
    },
    resetExclusions() {
      this.excludeVariants = false;
      this.excludedFromPrint.clear();
      this.filterCards();
    },
    toggleExcludeFromPrint(card) {
      if (this.excludedFromPrint.has(card.id)) {
        this.excludedFromPrint.delete(card.id);
      } else {
        this.excludedFromPrint.add(card.id);
      }
      this.filterCards();
    },
    viewCardVariants() {
      // TODO: Implement view card variants functionality
      alert('Card variants feature coming soon!');
    },
    viewCardDetails(card) {
      this.$router.push({ name: 'CardDetail', params: { cardId: card.id }});
    },
    async confirmDeleteCard() {
      if (confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
        try {
          await deleteCard(this.previewCard.id);
          this.clearForm();
          if (this.boardGameId) {
            const cards = await loadBoardGameCards(this.boardGameId);
            this.cards = cards;
          } else if (this.user) {
            const cards = await loadUserCards(this.user.uid);
            this.cards = cards;
          }
        } catch (error) {
          console.error('Error deleting card:', error);
          alert('Failed to delete card. Please try again.');
        }
      }
    },
    getGameImage(type) {
      // Default game image
      const defaultImage = 'https://storage.googleapis.com/rpg-framework-a73be.appspot.com/CastleIntro.jpg'
      
      // In the future, you can add specific images for different game types
      const gameTypeImages = {
        strategy: defaultImage,
        party: defaultImage,
        cooperative: defaultImage,
        card: defaultImage,
        board: defaultImage,
        other: defaultImage
      }
      
      return gameTypeImages[type] || defaultImage
    },
      updatePrompt(){
        // If there's a saved prompt, use that
        if (this.previewCard.prompt) {
          this.aiImagePrompt = this.previewCard.prompt;
          return;
        }
        
        // Otherwise generate a new prompt from card details
        var style = this.boardGame.cardStyle || '';
        var name = this.previewCard.name || '';
        var description = this.previewCard.description ||'';
        this.aiImagePrompt = style+' '+name+''+description
      },
    /** The cards the search and exclusions leave: one entry per card, no print copies. */
    applyFilters() {
        // console.log('fitlering')
      let filteredList = this.cards;
      
      // Apply search filter if there's a query
      if (this.searchQuery) {
        // console.log('filtering query')
        const query = this.searchQuery.toLowerCase().trim();
        
        filteredList = this.cards.filter(card => {
          // If no specific field is selected, search all fields
          if (!this.searchField) {
            return (
              card.name.toLowerCase().includes(query) ||
              (card.type && card.type.toLowerCase().includes(query)) ||
              (card.subtype && card.subtype.toLowerCase().includes(query)) ||
              card.description.toLowerCase().includes(query) ||
              (card.flavorText && card.flavorText.toLowerCase().includes(query))
            );
          }
          
          // If a specific field is selected, search only that field
          const fieldValue = card[this.searchField];
          
          // Handle cases where the field might be undefined or null
          if (!fieldValue) return false;
          
          return fieldValue.toLowerCase().includes(query);
        });
      }
      
      // Exclude variants if the checkbox is checked
      if (this.excludeVariants) {
        filteredList = filteredList.filter(card => !card.parent_id);
      }

      // Filter out cards that are excluded from print
      filteredList = filteredList.filter(card => !this.excludedFromPrint.has(card.id));

      return filteredList;
    },
      filterCards() {
      const filteredList = this.applyFilters();

      // In print mode, duplicate cards based on their repeats value
      if (this.isPrintMode) {
          const duplicatedCards = [];
          filteredList.forEach(card => {
              for (let i = 0; i < card.repeats; i++) {
                  const card_duplicate = { ...card };
                  card_duplicate.id = card.id+'-'+i+'';
                  duplicatedCards.push({ ...card_duplicate });
              }
          });
          this.filteredCards = duplicatedCards;
      } else {
        this.filteredCards = filteredList;
      }
      
      
      // console.log(this.filteredCards)
    },
    updatePreview() {
      // This method is called on keyup to update the preview card
      // Currently, it's not doing anything extra since v-model handles the updates
    },
    togglePrintMode() {
      this.isPrintMode = !this.isPrintMode;
      this.filterCards()
      // console.log(this.isPrintMode)
      // // Optional: Add print-specific logic here if needed
      // if (this.isPrintMode) {
      //   window.print(); // Trigger browser's print dialog
      // }
    },
    clearCards() {
      // Confirm before clearing all cards
      if (confirm('Are you sure you want to clear all cards? This cannot be undone.')) {
        if (this.user) {
          // If logged in, clear Firestore cards as well
          // Note: You would need to implement a method in firebase.js to delete all user's cards
          // For now, we'll just clear local cards
          this.cards = [];
        } else {
          // For non-logged in users, just clear local cards
          this.cards = [];
        }

        // Reset preview card and edit mode
        this.previewCard = {
          id: null,
          name: '',
          description: '',
          type: '',
          subtype: '',
          flavorText: '',
          icon: '',
          repeats: 1
        };
        this.selectedIconCategory = '';
        this.isEditMode = false;
      }
    },
    editCard(card) {
      // Load the selected card into the editor and show build section
      this.showBuildCard = true;
      this.previewCard = {
        id: card.id,
        name: card.name,
        description: card.description,
        type: card.type || '',
        subtype: card.subtype || '',
        flavorText: card.flavorText || '',
        icon: card.icon || '',
        repeats: card.repeats || 1,
        image_url: card.image_url || null,
        prompt: card.prompt || ''
      };
      
      this.isEditMode = true;
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    
    viewCard(card) {
      // Just load the card for viewing (no editing)
      this.previewCard = {
        id: card.id,
        name: card.name,
        description: card.description,
        type: card.type || '',
        subtype: card.subtype || '',
        flavorText: card.flavorText || '',
        icon: card.icon || '',
        repeats: card.repeats || 1,
        image_url: card.image_url || null,
        prompt: card.prompt || ''
      };
      
      this.isEditMode = false;
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    
    clearForm() {
      // Reset the form to empty state
      this.previewCard = {
        id: null,
        name: '',
        description: '',
        type: '',
        subtype: '',
        flavorText: '',
        icon: '',
        repeats: 1,
        image_url: null,
        prompt: ''
      };
      this.isEditMode = false;
    },
    async createCard() {
      console.log('create')
      console.log(this.previewCard.name.length)
      if(this.previewCard.name.length ==0){
        alert('Please add name to card');
      }
      if (this.previewCard.name) {
        if (!this.user) {
          alert('Please log in to save cards');
          return;
        }
        
        if (!this.canEdit && this.boardGameId) {
          alert('You do not have permission to create or edit cards for this board game');
          return;
        }

        const cardData = {
          name: this.previewCard.name,
          description: this.previewCard.description || '',
          type: this.previewCard.type || '',
          subtype: this.previewCard.subtype || '',
          flavorText: this.previewCard.flavorText || '',
          icon: this.previewCard.icon || '',
          image_url: this.previewCard.image_url || null,
          repeats: this.previewCard.repeats || 1,
          secondary: false,
          keys: [],
          // Add board game ID if available
          boardGameId: this.boardGameId || null,
          boardGameName: this.boardGameName || null,
          prompt: this.previewCard.prompt || ''
        };

        try {
          if (this.isEditMode) {
            // Update existing card
            console.log('update existing in edit mode')
            const index = this.cards.findIndex(card => card.id === this.previewCard.id);
            if (index !== -1) {
              // If the card has a Firestore ID, use it for update
              const cardToUpdate = this.cards[index];
              if (cardToUpdate.id) {
                // Implement update method in firebase.js if needed
                await updateCard(cardToUpdate.id, cardData);
              }
              this.filterCards()
              this.cards[index] = {
                ...cardToUpdate,
                ...cardData
              };
            }
            this.isEditMode = false;
          } else {
            // Create new card
            console.log('Create new card')
            const firestoreId = await saveCard(this.user.uid, cardData);
            const newCard = {
              ...cardData,
              id: this.cards.length > 0 ? Math.max(...this.cards.map(c => c.id)) + 1 : 1,
              firestoreId: firestoreId
            };
            console.log('add card')
            this.cards.push(newCard);
            this.filterCards()
          }
          
          // Reset preview card
          this.previewCard = {
            id: null,
            name: '',
            description: '',
            type: '',
            subtype: '',
            flavorText: '',
            icon: '',
            repeats: 1,
            image_url: null
          };
          this.selectedIconCategory = '';
        } catch (error) {
          console.error('Error saving card:', error);
          alert('Failed to save card. Please try again.');
        }
      }
    },
    
    // Image upload methods
    triggerImageUpload() {
      // Trigger hidden file input
      this.$refs.cardImageInput.click();
    },
    
    async uploadCardImage(event) {
      // Check if user is logged in
      if (!this.user) {
        alert('You need to be logged in to upload images');
        return;
      }

      // Get the file
      const file = event.target.files[0];
      
      // Validate file
      if (!file) return;
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File is too large. Maximum size is 5MB.');
        return;
      }

      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP).');
        return;
      }

      try {
        // Show loading state
        this.isUploadingImage = true;

        // Create a reference to the storage location
        const imageRef = storageRef(
          storage, 
          `card-images/${this.user.uid}/${Date.now()}_${file.name}`
        );

        // Upload the file
        const snapshot = await uploadBytes(imageRef, file);

        // Get the download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        // Update the preview card with the new image URL
        this.previewCard.image_url = downloadURL;

        // Clear the file input
        this.$refs.cardImageInput.value = null;
        
        // Hide loading state
        this.isUploadingImage = false;
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
        this.isUploadingImage = false;
      }
    },
    
    removeCardImage() {
      this.previewCard.image_url = null;
    },
    
    // AI Image generation methods
    async generateAIImages() {
      if (!this.aiImagePrompt.trim()) {
        alert('Please enter a prompt for the AI image generation');
        return;
      }
      
      try {
        this.aiImageGenerating = true;
        this.aiImageResults = [];
        
        // Generate AI images
        const imageUrls = await generateGameImage(this.aiImagePrompt);
        
        this.aiImageResults = imageUrls;
        this.selectedAIImage = null;
      } catch (error) {
        console.error('Error generating AI images:', error);
        alert('Failed to generate AI images. Please try again.');
      } finally {
        this.aiImageGenerating = false;
      }
    },
    
    /**
     * Make the deck's colour and black-and-white PDFs ahead of a download, so
     * the button is a link rather than a wait. Quick when nothing changed.
     */
    prepareCardPrints() {
      if (!this.boardGameId || !auth.currentUser) return null;
      if (!this.cardPrintsJob) {
        this.cardPrintsJob = sparkforgeApi.makeCardPrints(this.boardGameId)
          .catch(() => null)
          .finally(() => { this.cardPrintsJob = null; });
      }
      return this.cardPrintsJob;
    },
    /** Which cards a download prints: all of them, or just those a filter leaves. */
    downloadSelection() {
      const shown = this.applyFilters();
      return { ids: shown.map((card) => card.id), filtered: shown.length !== this.cards.length };
    },
    /**
     * Download the whole deck from its kept PDF, or just the cards shown. The tab opens before any
     * waiting, because browsers block a tab opened after an await.
     */
    async downloadDeck(variant) {
      if (!this.boardGameId || this.downloading) return;
      const tab = window.open('', '_blank');
      this.downloading = variant;
      try {
        const selection = this.downloadSelection();
        let url;
        if (selection.filtered) {
          // Just the cards shown: made for this download rather than kept.
          if (!selection.ids.length) throw new Error('No cards are shown to download.');
          const out = await sparkforgeApi.exportPdf(this.boardGameId, 'cards', {
            cardIds: selection.ids, variant
          });
          url = out.url;
        } else {
          await this.prepareCardPrints();
          const view = await sparkforgeApi.makeCardPrints(this.boardGameId);
          url = view[variant] && view[variant].url;
        }
        if (!url) throw new Error('The deck could not be printed.');
        if (tab) tab.location.href = url;
        else window.location.href = url;
      } catch (error) {
        if (tab) tab.close();
        console.error('Error downloading cards:', error);
        alert(error.message || 'Failed to download cards. Please try again.');
      } finally {
        this.downloading = null;
      }
    },

    async selectAIImage() {
      if (this.selectedAIImage && this.user) {
        try {
          // Show loading state
          this.isUploadingImage = true;
          // Save the prompt that generated this image
          this.previewCard.prompt = this.aiImagePrompt;
          
          // Convert data URL to Blob
          const response = await fetch(this.selectedAIImage);
          const blob = await response.blob();
          
          // Create a reference to the storage location
          const imageRef = storageRef(
            storage, 
            `card-images/${this.user.uid}/ai_${Date.now()}.jpg`
          );
          
          // Upload the blob to Firebase Storage
          const snapshot = await uploadBytes(imageRef, blob);
          
          // Get the download URL
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          // Update the preview card with the new image URL
          this.previewCard.image_url = downloadURL;
          
          // Close the modal using jQuery (if available)
          try {
            // Try to close using jQuery if available
            if (window.jQuery) {
              window.jQuery('#aiImageGeneratorModal').modal('hide');
            } else {
              // Fallback to data-bs-dismiss click
              const closeButton = document.querySelector('#aiImageGeneratorModal .btn-close');
              if (closeButton) {
                closeButton.click();
              }
            }
          } catch (error) {
            console.error('Error closing modal:', error);
          }
          
          // Reset AI image state
          this.aiImagePrompt = '';
          this.aiImageResults = [];
          this.selectedAIImage = null;
          
        } catch (error) {
          console.error('Error uploading AI image to storage:', error);
          alert('Failed to save the AI image. Please try again.');
        } finally {
          this.isUploadingImage = false;
        }
      } else if (!this.user) {
        alert('You need to be logged in to use AI-generated images');
      }
    }
  },
}
</script>

<style scoped>
/* Sizing lives here, not in an inline style, so the media query below can
   actually win. */
.game-header-image-container {
  overflow: hidden;
  border-radius: 12px;
}
.game-header-image-container img {
  display: block;
  width: 100%;
  max-height: 300px;
  object-fit: cover;
}


/* --- page header ---------------------------------------------------- */
.page-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.page-title {
  margin: 0;
  font-size: 1.5rem;
  min-width: 0;
}
.page-actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

@media (max-width: 767.98px) {
  .page-title { font-size: 1.15rem; width: 100%; }
  /* One scrollable row beats three full-width buttons stacked down the page. */
  .page-actions {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 2px;
  }
  .page-actions::-webkit-scrollbar { display: none; }
  .page-actions .btn { white-space: nowrap; flex: 0 0 auto; }
}

/* --- Edit / Deck switch (mobile) ------------------------------------ */
.mode-switch {
  display: flex;
  gap: 0.25rem;
  padding: 0.25rem;
  margin-bottom: 1rem;
  background: var(--bg-sketch, #eee8dd);
  border-radius: 10px;
  position: sticky;
  top: 0;
  z-index: 3;
}
.mode-btn {
  flex: 1;
  border: 0;
  background: transparent;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ink-light, #5a5a5a);
  transition: background 0.15s ease, color 0.15s ease;
}
.mode-btn.active {
  background: var(--bg-white, #fff);
  color: var(--ink, #2d2d2d);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.mode-count {
  display: inline-block;
  margin-left: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  opacity: 0.65;
}

/* Bootstrap has no d-md-grid, and the grid must stay a grid at >= md. */
@media (min-width: 768px) {
  .d-md-grid { display: grid !important; }
}

.deck-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 1.25rem;
  margin-top: 0.5rem;
}
@media (max-width: 576px) {
  .deck-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
}
.preview-body {
  display: flex;
  justify-content: center;
  padding: 1rem 0.5rem;
}

.bd-callout {
  padding: 1.25rem;
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  border: 1px solid var(--border-sketch, #eee);
  border-left-width: 0.25rem;
  border-radius: 12px;
  background-color: var(--bg-surface, #fff);
}

.bd-callout-info {
  border-left-color: var(--pop-blue, #5bc0de);
}

.card-actions {
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
}

.card-actions .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
}

.card {
  height: 262px;
  width: 187px;
  border: 1px solid #DDD;
  padding: 12px;
  margin: 2px;
  float: left;
  height: 360px;
  width: 240px;
  background-size: cover;
  background-position: center;
  position: relative;
}

.card.dark {
  color: white;
}
/*.card .details-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}
*/
.card .details-button {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

/* Show details button on card hover */
.card:hover .details-button {
  opacity: 1;
  visibility: visible;
}
.card-repeats-badge {
  display: inline-block;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 14px;
  font-weight: bold;
  margin-left: 5px;
  position: absolute;
  top: 10px;
  right: 10px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.card.dark .primary-block {
  background-color: rgba(0, 0, 0, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.card.dark .primary-header {
  background-color: rgba(0, 0, 0, 0.5);
}

.preview-card {
  /*height: 200px;*/
  /*border: 1px solid #007bff;*/
  /*background-color: #f8f9fa;*/
}

.primary-block{
    border-top: 1px solid #EEE;
    padding: 4px 0;
}

.ai-image-option {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 4px;
  transition: all 0.2s;
}

.ai-image-option:hover {
  border-color: var(--pop-blue, #007bff);
}

.ai-image-option.selected {
  border-color: var(--pop-green, #28a745);
  box-shadow: 0 0 0 2px rgba(52, 184, 114, 0.4);
}

.card-image-preview {
  margin-top: 10px;
}

.preview-card .primary-block {
  padding: 10px;
}

.preview-card .primary-header {
  font-weight: bold;
  margin-bottom: 10px;
}

.preview-card .primary-description {
  font-size: 0.9em;
  color: var(--ink-light, #666);
}
.primary-description{
  min-height: 120px;
}
.primary-flavor-text{

}

.preview-card .primary-type-subtype {
  font-size: 0.8em;
  color: var(--ink-muted, #888);
  margin-bottom: 5px;
  font-style: italic;
}

.preview-card .primary-flavor-text {
  font-size: 0.8em;
  color: var(--ink-muted, #777);
  margin-top: 5px;
  font-style: italic;
}

.cards .card {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.cards .card:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

@media print {
  /* Hide everything except the print-mode cards */
 /* * {
    visibility: hidden;
    display: none !important;
  }*/

  .navbar, .hide-print {
    display: none !important;
  }
  .btn{
    display: none !important;
  }

  .print-mode .container,
  .print-mode .cards {
    display: block !important;
    visibility: visible;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }

  .print-mode .card {
    page-break-inside: avoid;
    width: 240px;
    height: 360px;
    /*margin: 10px;*/
     width: 203.2mm;
    height: 50.8mm;
    padding: 10px;
    border: 1px solid #000;
    display: inline-block !important;
    overflow: hidden;
    visibility: visible;
    height: 262px;
    width: 187px;
  }

  .print-mode .cards {
    display: flex !important;
    flex-wrap: wrap;
    justify-content: center;
    position: static;
  }

  /* Page break after the 15th card */
  .print-mode .card:nth-child(15) {
    page-break-after: always;
    break-after: always;
    break-after: page;
  }
  .cards, .card{
    display:block !important;
  }

  .print-mode .card:hover {
    transform: none;
    box-shadow: none;
  }

  /* Ensure no extra elements are printed */
  .print-mode .btn-secondary,
  .print-mode .input-group,
  .print-mode .dropdown,
  .print-mode h1,
  .print-mode h3 {
    display: none !important;
  }
}
@media (max-width: 767.98px) {
  /* The art took roughly 40% of the first screen before anything actionable. */
  .game-header-image-container img {
    max-height: 27vh;
  }

  .bd-callout {
    padding: 0.7rem 0.85rem;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.85rem;
  }
  .bd-callout .d-flex {
    flex-wrap: nowrap;
    gap: 0.6rem;
    align-items: center;
  }
  .counts-compact { white-space: nowrap; }

  /* Full-size buttons in the card form are oversized on a phone. */
  .form-submit-row .btn {
    padding: 0.35rem 0.75rem;
    font-size: 0.88rem;
  }

  /* The checkbox and the reset button belong beside each other, not stacked. */
  .bd-callout .d-flex > div:last-child {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    white-space: nowrap;
  }
  .bd-callout .form-check-inline { margin-right: 0; }
  .bd-callout .btn { margin-left: 0 !important; font-size: 0.78rem; }
}
</style>